import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Skills.css';

gsap.registerPlugin(ScrollTrigger);

const skills = [
  {
    category: 'Frontend',
    icon: '🎨',
    color: '#6c63ff',
    technologies: [
      { name: 'React', level: 90 },
      { name: 'Next.js', level: 85 },
      { name: 'TypeScript', level: 80 },
      { name: 'Vue.js', level: 70 },
      { name: 'HTML5 / CSS3', level: 95 },
    ],
  },
  {
    category: 'Backend',
    icon: '🚀',
    color: '#00d4ff',
    technologies: [
      { name: 'Node.js', level: 88 },
      { name: 'NestJS', level: 82 },
      { name: 'Express.js', level: 85 },
      { name: 'Python', level: 65 },
      { name: 'REST APIs', level: 92 },
    ],
  },
  {
    category: 'Database',
    icon: '🗄️',
    color: '#ff6b6b',
    technologies: [
      { name: 'MongoDB', level: 85 },
      { name: 'PostgreSQL', level: 75 },
      { name: 'MySQL', level: 72 },
      { name: 'Redis', level: 60 },
    ],
  },
  {
    category: 'Tools & DevOps',
    icon: '🔧',
    color: '#ffd93d',
    technologies: [
      { name: 'Git & GitHub', level: 90 },
      { name: 'Docker', level: 65 },
      { name: 'Vercel / Netlify', level: 88 },
      { name: 'Vite / Webpack', level: 78 },
    ],
  },
];

function Skills() {
  const sectionRef = useRef(null);
  const labelRef   = useRef(null);
  const titleRef   = useRef(null);
  const cardsRef   = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set([labelRef.current, titleRef.current], { opacity: 0 });

      gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      })
      .to(labelRef.current, { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' })
      .to(titleRef.current, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.3');

      // Cards
      cardsRef.current.forEach((card, i) => {
        if (!card) return;
        gsap.set(card, { opacity: 0, y: 60, scale: 0.95 });
        gsap.to(card, {
          opacity: 1, y: 0, scale: 1,
          duration: 0.7, ease: 'power3.out',
          delay: i * 0.1,
          scrollTrigger: {
            trigger: card,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        });

        // Bars
        const bars = card.querySelectorAll('.skill-bar-fill');
        bars.forEach((bar) => {
          const lvl = bar.dataset.level;
          gsap.set(bar, { width: '0%' });
          gsap.to(bar, {
            width: `${lvl}%`,
            duration: 1.2,
            ease: 'power2.out',
            delay: i * 0.1 + 0.3,
            scrollTrigger: {
              trigger: card,
              start: 'top 88%',
              toggleActions: 'play none none none',
            },
          });
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="skills" className="skills" ref={sectionRef}>
      <div className="skills-bg-orb" />
      <div className="container">
        <div ref={labelRef} className="section-label">What I Know</div>
        <h2 ref={titleRef} className="section-title">
          Skills &amp; <span className="gradient-text">Technologies</span>
        </h2>
        <p className="section-subtitle">Tools I use daily to build fast and reliable software.</p>

        <div className="skills-grid">
          {skills.map((skill, i) => (
            <div
              key={skill.category}
              ref={(el) => (cardsRef.current[i] = el)}
              className="skill-card glass-card"
            >
              <div className="skill-header" style={{ '--color': skill.color }}>
                <div className="skill-icon-wrap" style={{ background: `${skill.color}18`, border: `1px solid ${skill.color}30` }}>
                  <span className="skill-icon">{skill.icon}</span>
                </div>
                <div>
                  <h3 className="skill-category">{skill.category}</h3>
                  <span className="skill-count">{skill.technologies.length} skills</span>
                </div>
              </div>

              <div className="skill-bars">
                {skill.technologies.map((tech) => (
                  <div key={tech.name} className="skill-item">
                    <div className="skill-item-header">
                      <span className="skill-name">{tech.name}</span>
                      <span className="skill-pct" style={{ color: skill.color }}>{tech.level}%</span>
                    </div>
                    <div className="skill-bar-track">
                      <div
                        className="skill-bar-fill"
                        data-level={tech.level}
                        style={{ background: `linear-gradient(90deg, ${skill.color}99, ${skill.color})` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="skill-accent-line" style={{ background: skill.color }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Skills;
